import { getMessaging, getToken, onMessage, Messaging } from "firebase/messaging";
import { app } from "./config";
import { doc, setDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "./config";

let messaging: Messaging | null = null;

// Initialiser FCM (uniquement si supporté)
export function initializeMessaging() {
  try {
    if (typeof window !== "undefined" && "serviceWorker" in navigator) {
      // ✅ Unregister tous les anciens Service Workers sauf Firebase
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          // Ne garder que firebase-messaging-sw
          if (!registration.active?.scriptURL.includes("firebase-messaging-sw")) {
            console.log("🗑️ Suppression ancien SW:", registration.active?.scriptURL);
            registration.unregister();
          }
        });
      });

      // ✅ Écouter les messages du Service Worker (pour navigation)
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'NAVIGATE') {
          console.log('🧭 Navigation vers:', event.data.url);
          window.location.href = event.data.url;
        }
      });

      messaging = getMessaging(app);
      return messaging;
    }
  } catch (error) {
    console.error("❌ FCM not supported:", error);
  }
  return null;
}

/** Sommes-nous dans l'APPLI ANDROID (Capacitor) plutot que dans un navigateur ? */
export function estAppliNative(): boolean {
  const cap: any = (window as any).Capacitor;
  return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
}

/**
 * Appli Android : le jeton vient du telephone lui-meme (notifications
 * natives, sonnerie en veille). Enregistre au meme endroit que les jetons web,
 * le serveur n'a rien a changer.
 */
async function jetonNatif(userId: string): Promise<string | null> {
  const plugin: any = (window as any).Capacitor?.Plugins?.NaturaPush;
  if (!plugin) return null;
  const { token } = await plugin.getToken();
  if (!token) return null;
  await setDoc(
    doc(collection(db, `users/${userId}/fcmTokens`), token),
    {
      token,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      platform: "android-app",
      userAgent: "NaturaBeldi-Android-App " + navigator.userAgent,
    },
    { merge: true },
  );
  return token;
}

// Demander permission et récupérer le token
export async function requestNotificationPermission(userId: string): Promise<string | null> {
  if (estAppliNative()) {
    try {
      return await jetonNatif(userId);
    } catch (e) {
      console.error("❌ Jeton appli Android :", e);
      return null;
    }
  }
  try {
    if (!messaging) {
      messaging = initializeMessaging();
    }

    if (!messaging) {
      console.error("❌ Messaging not available");
      return null;
    }

    // ✅ Enregistrer notre Service Worker Firebase (pas celui de Workbox)
    if ("serviceWorker" in navigator) {
      try {
        const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
        console.log("✅ Service Worker Firebase enregistré:", registration);
      } catch (error) {
        console.error("❌ Erreur enregistrement Service Worker:", error);
      }
    }

    // Demander permission
    const permission = await Notification.requestPermission();
    
    if (permission !== "granted") {
      console.log("❌ Notification permission denied");
      return null;
    }

    // Récupérer le token FCM
    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    });

    if (token) {
      console.log("✅ FCM Token:", token);

      // Sauvegarder le token dans Firestore
      await setDoc(
        doc(collection(db, `users/${userId}/fcmTokens`), token),
        {
          token,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          platform: navigator.platform,
          userAgent: navigator.userAgent,
        },
        { merge: true }
      );

      return token;
    }

    return null;
  } catch (error) {
    console.error("❌ Error getting FCM token:", error);
    return null;
  }
}

// Écouter les messages en premier plan
export function onForegroundMessage(callback: (payload: any) => void) {
  if (!messaging) {
    messaging = initializeMessaging();
  }

  if (!messaging) {
    console.error("❌ Messaging not available for foreground messages");
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    console.log("📬 Message reçu en premier plan:", payload);
    callback(payload);
  });
}
